import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Children, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { extractErrorMessage, resources } from '../../core/services';
import type { Entity } from '../../core/types/data-models';
import { formatEntityTypeLabel } from '../../shared/components/entityTypeLabel';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

/** Wraps every case-insensitive occurrence of `term` in a plain string with a yellow highlight. */
function highlightTerm(text: string, term: string): ReactNode {
  if (!term.trim()) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.toLowerCase() === term.toLowerCase() ? (
      <Box key={i} component="mark" sx={{ bgcolor: '#fff59d', color: '#000', borderRadius: '2px' }}>
        {part}
      </Box>
    ) : (
      part
    ),
  );
}

/** Applies `highlightTerm` to a markdown node's direct string children, leaving nested elements untouched. */
function highlightChildren(children: ReactNode, term: string): ReactNode {
  return Children.map(children, (child) => (typeof child === 'string' ? highlightTerm(child, term) : child));
}

/** ReactMarkdown `components` override highlighting `term` in the common text-bearing block/inline elements. */
function highlightMarkdownComponents(term: string) {
  return {
    p: ({ children }: { children?: ReactNode }) => <p>{highlightChildren(children, term)}</p>,
    li: ({ children }: { children?: ReactNode }) => <li>{highlightChildren(children, term)}</li>,
    strong: ({ children }: { children?: ReactNode }) => <strong>{highlightChildren(children, term)}</strong>,
    em: ({ children }: { children?: ReactNode }) => <em>{highlightChildren(children, term)}</em>,
  };
}

type RelationView = {
  key: string;
  direction: 'outgoing' | 'incoming';
  relationType: string;
  relationDescription: string | null;
  otherEntityName: string;
};

/**
 * Cross-document detail view for one project entity: every
 * `DocumentSectionEntity` link row pointing at it is a "mention", so this
 * fetches all of them rather than showing just the section that was clicked.
 */
export function EntityDetailsDialog({
  projectId,
  entity,
  canEdit,
  onClose,
}: {
  projectId: number;
  /** The project entity to show. */
  entity: Entity | null;
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [managedSubmitting, setManagedSubmitting] = useState(false);
  const [managedError, setManagedError] = useState<string | null>(null);

  const entityId = entity?.id ?? null;
  const open = entity !== null;

  const mentionsQuery = useQuery({
    queryKey: ['entity-mentions', projectId, entityId],
    queryFn: () =>
      resources.documentSectionEntities.list({
        filter: { projectId: [projectId], entityId: [entityId!] },
        pageSize: 500,
      }),
    enabled: open && entityId != null,
  });
  const mentions = mentionsQuery.data?.data ?? [];
  // Both are properties of the entity itself now — no merging across mentions.
  const isManaged = entity?.isManaged ?? false;
  const description = entity?.entityDescription;

  const sectionIds = Array.from(new Set(mentions.map((m) => m.documentSectionId).filter((id): id is number => id !== null)));
  const sectionsQuery = useQuery({
    queryKey: ['entity-mention-sections', projectId, sectionIds],
    queryFn: () =>
      resources.documentSections.list({ filter: { projectId: [projectId], id: sectionIds }, pageSize: sectionIds.length }),
    enabled: sectionIds.length > 0,
  });
  const sectionById = new Map((sectionsQuery.data?.data ?? []).map((s) => [s.id, s]));

  const documentIds = Array.from(new Set(mentions.map((m) => m.documentId)));
  const documentsQuery = useQuery({
    queryKey: ['entity-mention-documents', projectId, documentIds],
    queryFn: () =>
      resources.documents.list({ filter: { projectId: [projectId], id: documentIds }, pageSize: documentIds.length }),
    enabled: documentIds.length > 0,
  });
  const documentById = new Map((documentsQuery.data?.data ?? []).map((d) => [d.id, d]));

  // Relations are project-level, so both directions come straight off the
  // entity id — no per-mention fan-out and no cross-section dedupe: the
  // (source, target, type) triple is unique on the table.
  const outgoingQuery = useQuery({
    queryKey: ['entity-relations-outgoing', projectId, entityId],
    queryFn: () =>
      resources.entityRelations.list({
        filter: { projectId: [projectId], sourceEntityId: [entityId!] },
        include: ['targetEntity'],
        pageSize: 500,
      }),
    enabled: open && entityId != null,
  });
  const incomingQuery = useQuery({
    queryKey: ['entity-relations-incoming', projectId, entityId],
    queryFn: () =>
      resources.entityRelations.list({
        filter: { projectId: [projectId], targetEntityId: [entityId!] },
        include: ['sourceEntity'],
        pageSize: 500,
      }),
    enabled: open && entityId != null,
  });

  const relations: RelationView[] = [
    ...(outgoingQuery.data?.data ?? []).flatMap((r) =>
      r.targetEntity
        ? [{
            key: `out-${r.id}`,
            direction: 'outgoing' as const,
            relationType: r.relationType,
            relationDescription: r.relationDescription,
            otherEntityName: r.targetEntity.entityName,
          }]
        : [],
    ),
    ...(incomingQuery.data?.data ?? []).flatMap((r) =>
      r.sourceEntity
        ? [{
            key: `in-${r.id}`,
            direction: 'incoming' as const,
            relationType: r.relationType,
            relationDescription: r.relationDescription,
            otherEntityName: r.sourceEntity.entityName,
          }]
        : [],
    ),
  ];

  const relatedContent = mentions
    .map((m) => ({
      mention: m,
      document: documentById.get(m.documentId),
      section: m.documentSectionId === null ? undefined : sectionById.get(m.documentSectionId),
    }))
    // Keep document-level mentions (no section) alongside section mentions whose section resolved.
    .filter((r) => r.section || r.mention.documentSectionId === null);

  const handleManagedToggle = async () => {
    if (!entityId) return;
    setManagedSubmitting(true);
    setManagedError(null);
    try {
      await resources.setEntityManaged(entityId, !isManaged);
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'project-entities' || q.queryKey[0] === 'entity-mentions',
      });
    } catch (e) {
      setManagedError(extractErrorMessage(e));
    } finally {
      setManagedSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '85vh' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        {entity?.entityName ?? 'Entity Details'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Chip label={entity ? formatEntityTypeLabel(entity.entityType) : ''} size="small" variant="outlined" />
            {canEdit ? (
              <Button
                size="small"
                variant={isManaged ? 'contained' : 'outlined'}
                color="warning"
                startIcon={
                  managedSubmitting ? <CircularProgress size={14} color="inherit" /> : <BookmarkIcon fontSize="small" />
                }
                onClick={handleManagedToggle}
                disabled={managedSubmitting || !entityId}
              >
                {isManaged ? 'Managed' : 'Mark as managed'}
              </Button>
            ) : (
              isManaged && (
                <Chip icon={<BookmarkIcon fontSize="small" />} label="Managed" size="small" color="warning" variant="outlined" />
              )
            )}
          </Stack>
          {managedError && (
            <Typography variant="caption" color="error">
              {managedError}
            </Typography>
          )}

          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}

          <Divider />

          <Box>
            <Typography variant="overline" color="text.secondary">
              Related Entities
            </Typography>
            {mentionsQuery.isPending ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : relations.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                No relations found.
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ mt: 1 }}>
                {relations.map((r) => (
                  <Stack key={r.key} direction="row" spacing={1} alignItems="flex-start">
                    <Tooltip title={r.direction === 'outgoing' ? 'Outgoing' : 'Incoming'}>
                      {r.direction === 'outgoing' ? (
                        <ArrowForwardIcon sx={{ fontSize: 16, mt: '2px', color: 'text.disabled', flexShrink: 0 }} />
                      ) : (
                        <ArrowBackIcon sx={{ fontSize: 16, mt: '2px', color: 'text.disabled', flexShrink: 0 }} />
                      )}
                    </Tooltip>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 700 }}>
                          {formatEntityTypeLabel(r.relationType)}
                        </Box>{' '}
                        {r.otherEntityName}
                      </Typography>
                      {r.relationDescription && (
                        <Typography variant="caption" color="text.secondary">
                          {r.relationDescription}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="overline" color="text.secondary">
              Related Content
            </Typography>
            {mentionsQuery.isPending ? (
              <Stack alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Stack>
            ) : relatedContent.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                No related sections found.
              </Typography>
            ) : (
              <Stack spacing={2} sx={{ mt: 1, maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                {relatedContent.map(({ mention, document, section }) => (
                  <Box key={mention.id}>
                    <Typography variant="body2" fontWeight={700}>
                      {document?.name ?? 'Untitled document'}
                      {section?.title && <> — {highlightTerm(section.title, entity?.entityName ?? '')}</>}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
                      {formatDate(mention.createdAt)}
                    </Typography>
                    {section?.content ? (
                      <Box
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                          '& p': { mt: 0, mb: 1 },
                          '& ul, & ol': { pl: 2.5, mb: 1 },
                          '& li': { mb: 0.25 },
                        }}
                      >
                        <ReactMarkdown components={highlightMarkdownComponents(entity?.entityName ?? '')}>
                          {section.content}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                        No content.
                      </Typography>
                    )}
                    <Divider sx={{ mt: 1.5 }} />
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
